import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { API_BASE_URL } from "../config/env";
import { clearAuthData } from "../utils/authUtils";

export interface BaseQueryConfig {
  /** Whether to use admin endpoints (/admin/api) or regular endpoints (/api) */
  useAdminEndpoint?: boolean;
  /** Whether to include debug logging for API requests */
  enableDebugLogging?: boolean;
  /** Whether to include detailed content-type and accept headers */
  includeDetailedHeaders?: boolean;
}

/**
 * Creates a reusable base query with authentication and error handling
 * @param config Configuration options for the base query
 * @returns Configured fetchBaseQuery instance with 401 handling
 */
export const createAuthenticatedBaseQuery = (config: BaseQueryConfig = {}): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> => {
  const {
    useAdminEndpoint = false,
    enableDebugLogging = false,
    includeDetailedHeaders = false,
  } = config;

  const baseUrl = useAdminEndpoint
    ? `${API_BASE_URL}/admin/api`
    : `${API_BASE_URL}/api`;

  const baseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      // Get authentication token from localStorage
      const token = localStorage.getItem("token");
      
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      } else if (enableDebugLogging) {
        console.warn("No authentication token found in localStorage");
      }
      
      // Add detailed headers if requested
      if (includeDetailedHeaders) {
        headers.set("content-type", "application/json");
        headers.set("accept", "application/json");
      }
      
      // Debug logging if enabled
      if (enableDebugLogging) {
        console.debug("API Request Headers:", {
          authorization: headers.get("authorization") ? "Bearer [TOKEN_PRESENT]" : "NO_TOKEN",
          "content-type": headers.get("content-type"),
          accept: headers.get("accept"),
        });
      }
      
      return headers;
    },
  });

  // Wrapper that handles 401 errors globally
  return async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions);
    
    // Check if the response is a 401 Unauthorized
    if (result.error && result.error.status === 401) {
      if (enableDebugLogging) {
        console.warn("401 Unauthorized response received, clearing auth data");
      }
      
      // Clear authentication data
      clearAuthData();
    }
    
    return result;
  };
};

// Pre-configured base queries for common use cases
export const adminBaseQuery = createAuthenticatedBaseQuery({
  useAdminEndpoint: true,
  enableDebugLogging: true,
  includeDetailedHeaders: true,
});

export const authBaseQuery = createAuthenticatedBaseQuery({
  useAdminEndpoint: false,
  enableDebugLogging: false,
  includeDetailedHeaders: false,
}); 

export const uploadBaseQuery = createAuthenticatedBaseQuery({
  useAdminEndpoint: false,
  enableDebugLogging: false,
  includeDetailedHeaders: false,
}); 